import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { VelavuPermission } from "velavu-js-api";
import { TryResolveData, useVelavuModal } from "../../elements/velavu-modal";
import VelavuAlertModal from "../../elements/velavu-alert-modal/velavu-alert-modal";
import VelavuAlertModalTitle from "../../elements/velavu-alert-modal/velavu-alert-modal-title";
import VelavuAlertModalContent from "../../elements/velavu-alert-modal/velavu-alert-modal-content";
import VelavuAlertModalActions from "../../elements/velavu-alert-modal/velavu-alert-modal-actions";
import VelavuButton from "../../elements/velavu-button";
import * as styles from "./settings-modal.module.scss";
import SectionSidebar, {
	SectionSidebarItem,
} from "../../elements/section-sidebar";
import Divider from "../../elements/divider";
import SimpleObservable from "../../helper/simple-observable";
import { makeStyleable } from "../../helper/icon-helper";
import ChangePasswordModal from "./change-password-modal";
import SettingsAlerts from "./settings-alerts";
import SettingsOrganization from "./settings-organization";
import SettingsTokens from "./settings-tokens";
import SettingsUser from "./settings-user";
import SettingsBroker from "./settings-broker";
import SettingsBilling from "./settings-billing/settings-billing";
import SettingsIntegrations from "./settings-integrations";
import IconVelavuUser from "../../dynamicicons/icon-velavu-user";
import IconOrganization from "../../dynamicicons/icon-organization";
import IconTeam from "../../dynamicicons/icon-team";
import IconBilling from "../../dynamicicons/icon-billing";
import IconVelavuDevices from "../../dynamicicons/icon-velavu-devices";
import IconToken from "../../dynamicicons/icon-token";
import IconVelavuAlerts from "../../dynamicicons/icon-velavu-alerts";
import IconLink from "../../dynamicicons/icon-link";
import IconInventory from "../../dynamicicons/icon-inventory";
import {
	HasPermissionFunction,
	useSignInContext,
} from "../sign-in-context/sign-in-context";
import SettingsDevicesV2 from "./settings-devices";
import SettingsTeam from "./settings-team";

export enum SettingsTab {
	User,
	Devices,
	Alerts,
	Organization,
	Team,
	Integrations,
	Billing,
	Tokens,
	Broker,
}

const SETTINGS_TAB_PERMISSIONS: Readonly<
	Record<SettingsTab, readonly VelavuPermission[]>
> = {
	[SettingsTab.User]: ["users:read"],
	[SettingsTab.Devices]: ["rules:read", "rules:write", "rules:write-config"],
	[SettingsTab.Alerts]: [],
	[SettingsTab.Organization]: ["organization:read", "organization:write"],
	[SettingsTab.Team]: ["users:read", "users:write", "users:delete"],
	[SettingsTab.Integrations]: ["integrations:read", "integrations:write"],
	[SettingsTab.Billing]: ["billing:read"],
	[SettingsTab.Tokens]: ["api-tokens:read"],
	[SettingsTab.Broker]: [
		"backends:read",
		"backends:write",
		"backends:delete",
		"mqtt:read",
		"mqtt:write",
	],
};

/**
 * Returns whether a settings tab is accessible given the user's permissions.
 */
export function isTabAccessible(
	tab: SettingsTab,
	hasPermission: HasPermissionFunction,
): boolean {
	return SETTINGS_TAB_PERMISSIONS[tab].every((permission) =>
		hasPermission(permission),
	);
}

export default function SettingsModal(props: {
	onTryResolve: SimpleObservable<TryResolveData<any>>;
	initialTabKey?: SettingsTab;
}) {
	const { t } = useTranslation();
	const { hasPermission } = useSignInContext();

	const sidebarItems = useMemo<SectionSidebarItem<SettingsTab>[]>(() => {
		const items: SectionSidebarItem<SettingsTab>[] = [
			{
				key: SettingsTab.User,
				label: t("settings.tabs.user"),
				icon: makeStyleable(IconVelavuUser),
			},
			{
				key: SettingsTab.Devices,
				label: t("settings.tabs.devices"),
				icon: makeStyleable(IconVelavuDevices),
			},
			{
				key: SettingsTab.Alerts,
				label: t("settings.tabs.alerts"),
				icon: makeStyleable(IconVelavuAlerts),
			},
			{
				key: SettingsTab.Organization,
				label: t("settings.tabs.organization"),
				icon: makeStyleable(IconOrganization),
			},
			{
				key: SettingsTab.Team,
				label: t("settings.tabs.team"),
				icon: makeStyleable(IconTeam),
			},
			{
				key: SettingsTab.Integrations,
				label: t("settings.tabs.integrations"),
				icon: makeStyleable(IconLink),
			},
			{
				key: SettingsTab.Billing,
				label: t("settings.tabs.billing"),
				icon: makeStyleable(IconBilling),
			},
			{
				key: SettingsTab.Tokens,
				label: t("settings.tabs.tokens"),
				icon: makeStyleable(IconToken),
			},
			{
				key: SettingsTab.Broker,
				label: t("settings.tabs.broker"),
				icon: makeStyleable(IconInventory),
			},
		];

		return items.filter((item) => isTabAccessible(item.key, hasPermission));
	}, [t, hasPermission]);

	const [tabKey, setTabKey] = useState<SettingsTab>(
		props.initialTabKey ?? SettingsTab.User,
	);
	const [isDirty, setDirty] = useState(false);

	// Fall back to the first visible tab if the current/initial tab is hidden
	const selectedTabKey = sidebarItems.some((item) => item.key === tabKey)
		? tabKey
		: sidebarItems[0].key;

	const { pushModal } = useVelavuModal();
	const changePassword = useCallback(
		() => pushModal((close) => <ChangePasswordModal onClose={close} />),
		[pushModal],
	);

	const updateTabKey = useCallback(
		(key: SettingsTab) => {
			//If we have unsaved data, prevent the user from going back and display a confirmation dialog
			if (isDirty) {
				pushModal<boolean>((close) => (
					<VelavuAlertModal>
						<VelavuAlertModalTitle>
							{t("settings.discard_modal.title")}
						</VelavuAlertModalTitle>

						<VelavuAlertModalContent>
							{t("settings.discard_modal.content")}
						</VelavuAlertModalContent>

						<VelavuAlertModalActions>
							<VelavuButton
								label={t("settings.discard_modal.cancel")}
								variant="outlined"
								fullWidth
								onClick={() => close(false)}
							/>

							<VelavuButton
								label={t("settings.discard_modal.discard")}
								variant="normal"
								fullWidth
								onClick={() => close(true)}
							/>
						</VelavuAlertModalActions>
					</VelavuAlertModal>
				)).then((result) => result && setTabKey(key));
			} else {
				setTabKey(key);
			}
		},
		[isDirty, setTabKey, pushModal, t],
	);

	const propsOnTryResolve = props.onTryResolve;
	useEffect(() => {
		const listener: (data: TryResolveData<any>) => void = (data) => {
			if (isDirty) {
				data.cancel();
				pushModal<boolean>((close) => (
					<VelavuAlertModal>
						<VelavuAlertModalTitle>
							{t("settings.discard_modal.title")}
						</VelavuAlertModalTitle>

						<VelavuAlertModalContent>
							{t("settings.discard_modal.content")}
						</VelavuAlertModalContent>

						<VelavuAlertModalActions>
							<VelavuButton
								label={t("settings.discard_modal.cancel")}
								variant="outlined"
								fullWidth
								onClick={() => close(false)}
							/>

							<VelavuButton
								label={t("settings.discard_modal.discard")}
								variant="normal"
								fullWidth
								onClick={() => close(true)}
							/>
						</VelavuAlertModalActions>
					</VelavuAlertModal>
				)).then((result) => result && data.initiateResolve());
			}
		};
		propsOnTryResolve.subscribe(listener);
		return () => propsOnTryResolve.unsubscribe(listener);
	}, [isDirty, pushModal, propsOnTryResolve, t]);

	return (
		<div className={styles.container}>
			<Divider />

			<div className={styles.splitContainer}>
				<SectionSidebar
					className={styles.sidebar}
					items={sidebarItems}
					selectedKey={selectedTabKey}
					onSelectKey={updateTabKey}
				/>

				<div className={styles.detail}>
					{selectedTabKey === SettingsTab.User && (
						<SettingsUser
							isDirty={isDirty}
							setDirty={setDirty}
							changePassword={changePassword}
						/>
					)}
					{selectedTabKey === SettingsTab.Devices && (
						<SettingsDevicesV2
							isDirty={isDirty}
							setDirty={setDirty}
						/>
					)}

					{selectedTabKey === SettingsTab.Alerts && (
						<SettingsAlerts isDirty={isDirty} setDirty={setDirty} />
					)}
					{selectedTabKey === SettingsTab.Organization && (
						<SettingsOrganization />
					)}
					{selectedTabKey === SettingsTab.Team && <SettingsTeam />}
					{selectedTabKey === SettingsTab.Integrations && (
						<SettingsIntegrations />
					)}
					{selectedTabKey === SettingsTab.Billing && (
						<SettingsBilling />
					)}
					{selectedTabKey === SettingsTab.Tokens && (
						<SettingsTokens />
					)}
					{selectedTabKey === SettingsTab.Broker && (
						<SettingsBroker isDirty={isDirty} setDirty={setDirty} />
					)}
				</div>
			</div>
		</div>
	);
}
